/** @format — Compact Leaflet preview for tracking.map AG-UI card */

import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, ZoomControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import dockerImg from "../../../assets/Tracking/docker-img.svg";

function MiniMapBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    const valid = (points ?? []).filter((p) => Array.isArray(p) && p.length === 2);
    if (valid.length > 1) {
      map.fitBounds(valid, { padding: [20, 20], maxZoom: 6, animate: false });
    } else if (valid.length === 1) {
      map.setView(valid[0], 5, { animate: false });
    }
  }, [map, points]);
  return null;
}

const vesselIcon = L.divIcon({
  html: `<div style="width:20px;height:20px;border-radius:50%;background:#fff;border:2px solid #1D4ED8;box-shadow:0 4px 8px rgba(0,0,0,0.2);display:flex;align-items:center;justify-content:center;padding:1px;"><img src="${dockerImg}" alt="" style="width:100%;height:100%;object-fit:contain" /></div>`,
  className: "",
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const destIcon = L.divIcon({
  html: `<div style="width:10px;height:10px;background:#fff;border:2px solid #2563EB;border-radius:1px;"></div>`,
  className: "",
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

const originIcon = L.divIcon({
  html: `<div style="width:10px;height:10px;background:#22C55E;border:2px solid #fff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.2);"></div>`,
  className: "",
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

export default function TrackingMiniMap({ model, className = "" }) {
  const center = model?.vesselPosition ?? model?.originPosition ?? [29.76, -95.37];

  const points = useMemo(() => {
    const list = [];
    if (model?.originPosition) list.push(model.originPosition);
    if (model?.vesselPosition) list.push(model.vesselPosition);
    if (model?.destinationPosition) list.push(model.destinationPosition);
    for (const p of model?.routeCompletedPath ?? []) list.push(p);
    for (const p of model?.routeRemainingPath ?? []) list.push(p);
    return list;
  }, [model]);

  const hasRoute =
    (model?.routeCompletedPath?.length ?? 0) > 0 || (model?.routeRemainingPath?.length ?? 0) > 0;

  return (
    <div className={`relative isolate z-0 h-[153px] w-full overflow-hidden bg-[#E8F4FC] ${className}`}>
      <MapContainer
        center={center}
        zoom={4}
        className="h-full w-full"
        zoomControl={false}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        touchZoom={false}
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <ZoomControl position="topleft" />
        <MiniMapBounds points={points} />
        {model?.routeCompletedPath?.length > 1 ? (
          <Polyline positions={model.routeCompletedPath} color="#2563EB" weight={3} opacity={0.95} />
        ) : null}
        {model?.routeRemainingPath?.length > 1 ? (
          <Polyline
            positions={model.routeRemainingPath}
            color="#2563EB"
            weight={2}
            dashArray="6 6"
            opacity={0.85}
          />
        ) : null}
        {!hasRoute && model?.originPosition && model?.destinationPosition ? (
          <Polyline
            positions={[model.originPosition, model.vesselPosition ?? model.originPosition, model.destinationPosition].filter(Boolean)}
            color="#2563EB"
            weight={2}
            dashArray="5 5"
            opacity={0.85}
          />
        ) : null}
        {model?.originPosition ? <Marker position={model.originPosition} icon={originIcon} /> : null}
        {model?.vesselPosition ? <Marker position={model.vesselPosition} icon={vesselIcon} /> : null}
        {model?.destinationPosition ? (
          <Marker position={model.destinationPosition} icon={destIcon} />
        ) : null}
      </MapContainer>
      {model?.locationName ? (
        <div
          className="absolute left-[52%] top-[30%] max-w-[120px] rounded-lg border border-[#d0d5dd] bg-white px-2 py-1 shadow-sm pointer-events-none"
          style={{
            boxShadow: "0px 3px 2px -2px rgba(0,0,0,0.06), 0px 5px 3px -2px rgba(0,0,0,0.02)",
          }}
        >
          <p className="text-[10px] text-center text-[#344054] truncate">{model.locationName}</p>
        </div>
      ) : null}
    </div>
  );
}
